import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSchoolByCode, getSortedFallbackSchools } from '../../config/schoolConfig';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../utils/axiosConfig';
import GoogleLoginButton from '../LoginWithGoogle/GoogleLoginButton';
import ExternalLink from '../common/ExternalLink';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolCode } = useParams();
  const { checkAuth, setTokens } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    agreeToTerms: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true); // Toggle between login and register
  const [showOptions, setShowOptions] = useState(true); // Control whether to show the two boxes or the form
  const [oauthErrorProcessed, setOauthErrorProcessed] = useState(false); // Track if we've processed OAuth error
  const [showPassword, setShowPassword] = useState(false); // Toggle password visibility

  // Fetch universities from backend
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await axios.get('/api/v1/university');
        if (
          response.data &&
          response.data.universities &&
          Array.isArray(response.data.universities)
        ) {
          const mappedUniversities = response.data.universities.map((uni) => {
            // Extract domain strings from email_domains array (now returns objects with email_domain and domain_type)
            let domains = [];
            if (Array.isArray(uni.email_domains) && uni.email_domains.length > 0) {
              // New format: array of {email_domain, domain_type} objects
              domains = uni.email_domains.map((d) => (typeof d === 'string' ? d : d.email_domain));
            } else if (uni.email_domain) {
              // Fallback to single email_domain
              domains = [uni.email_domain];
            }

            return {
              id: uni.university_id || uni.id,
              name: uni.name,
              auth_type: uni.auth_type || 'password',
              domain: uni.email_domain || uni.domain, // Primary domain for backwards compatibility
              domains: domains, // Array of all accepted email domain strings
            };
          });

          // Sort universities: "Other" at the bottom, rest alphabetically
          const sortedUniversities = mappedUniversities.sort((a, b) => {
            if (a.name === 'Other') return 1;
            if (b.name === 'Other') return -1;
            return a.name.localeCompare(b.name);
          });

          setUniversities(sortedUniversities);
        }
      } catch (err) {
        console.error('Failed to fetch universities:', err);
        // Use environment-configurable fallback schools
        setUniversities(getSortedFallbackSchools());
      }
    };

    fetchUniversities();
  }, []);

  // Check for OAuth callback errors
  useEffect(() => {
    if (location.state?.error && !oauthErrorProcessed) {
      setError(location.state.error);
      setOauthErrorProcessed(true); // Mark as processed

      if (location.state.showAuthForm) {
        setShowOptions(false);
        setIsLogin(true); // Default to login mode for OAuth errors
      }

      // Clear the location state after processing
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, oauthErrorProcessed, location.pathname]);

  // Auto-select university if coming from OAuth error and we can determine it
  useEffect(() => {
    if (oauthErrorProcessed && universities.length > 0 && !selectedUniversity) {
      // If it's a Google OAuth error, try to auto-select the Google OAuth university
      const googleUniversity = universities.find((uni) => uni.auth_type === 'google_oauth');
      if (googleUniversity) {
        setSelectedUniversity(googleUniversity);
      }
    }
  }, [oauthErrorProcessed, universities, selectedUniversity]);

  // Auto-select university and set registration mode if coming from custom URL
  useEffect(() => {
    if (schoolCode && universities.length > 0 && !selectedUniversity) {
      const schoolInfo = getSchoolByCode(schoolCode);
      if (schoolInfo) {
        const university = universities.find(
          (uni) => uni.name.toLowerCase() === schoolInfo.name.toLowerCase()
        );
        if (university) {
          setSelectedUniversity(university);
          setIsLogin(false); // Set to registration mode
          setShowOptions(false); // Skip the options selection
        }
      }
    }
  }, [schoolCode, universities, selectedUniversity]);

  // Set registration mode if on /register path
  useEffect(() => {
    if (location.pathname === '/register' || location.pathname.startsWith('/register/')) {
      setIsLogin(false);
      // Only skip options if we have a school code (custom URL)
      if (schoolCode) {
        setShowOptions(false);
      }
    }
  }, [location.pathname, schoolCode]);

  const handleUniversitySelect = (e) => {
    const schoolId = e.target.value;
    const university = universities.find((u) => u.id && String(u.id) === String(schoolId));
    setSelectedUniversity(university);
    setError(''); // Clear any existing errors when changing university
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateEmail = useCallback(
    (email) => {
      if (!selectedUniversity) return false;
      if (selectedUniversity.name === 'Other') return true;
      const emailDomain = email.split('@')[1];
      // Check if email domain matches any of the university's accepted domains
      return selectedUniversity.domains && selectedUniversity.domains.includes(emailDomain);
    },
    [selectedUniversity]
  );

  // Use useCallback to ensure this function is stable across renders
  const handleSubmit = useCallback(
    async (e) => {
      // Prevent the default form submission which causes page refresh
      e.preventDefault();

      setError('');

      if (!validateEmail(formData.email)) {
        const domainsText =
          selectedUniversity.domains && selectedUniversity.domains.length > 1
            ? `one of these domains: ${selectedUniversity.domains.join(', ')}`
            : `the domain ${selectedUniversity.domain}`;
        setError(`Please use an email with ${domainsText}`);
        return;
      }

      // Check Terms agreement for registration
      if (!isLogin && !formData.agreeToTerms) {
        setError('Please agree to the Terms of Service and Privacy Policy');
        return;
      }

      setLoading(true);

      try {
        if (isLogin) {
          // Handle login
          const response = await axios.post('/api/v1/auth/login', {
            email: formData.email,
            password: formData.password,
            remember_me: formData.rememberMe,
          });

          // Set tokens using the new auth context method
          if (response.data.access_token) {
            setTokens(response.data.access_token);
          }

          // Store user ID for compatibility (some components might still use it)
          if (response.data.user?.id) {
            localStorage.setItem('userID', response.data.user.id);
          }

          // Navigate to home page
          navigate('/home');
        } else {
          // Handle registration
          await axios.post('/api/v1/auth/register/init', { email: formData.email });
          navigate('/check-email', {
            state: {
              email: formData.email,
              message: 'Please check your email to verify your address and complete registration.',
            },
          });
        }
      } catch (err) {
        console.error('Auth error:', err);

        if (
          err.response?.status === 401 &&
          err.response?.data?.detail?.includes('email not verified')
        ) {
          navigate('/check-email', {
            state: {
              email: formData.email,
              message:
                'Your email has not been verified. Please check your email for a verification link.',
            },
          });
        } else if (
          err.response?.status === 400 &&
          err.response?.data?.detail?.includes('already registered')
        ) {
          setError('This email is already registered. Please sign in instead.');
          setIsLogin(true); // Switch to login mode
        } else {
          // Keep the form data intact for login failures
          const errorMessage =
            err.response?.data?.detail ||
            (isLogin
              ? 'Login failed. Please check your credentials.'
              : 'Registration failed. Please try again.');

          console.log('Setting error:', errorMessage);
          setError(errorMessage);

          // Don't reset the form - keep the entered values
          if (isLogin) {
            // Clear only the password field for security reasons but keep the email and rememberMe
            setFormData((prev) => ({
              ...prev,
              password: '',
            }));
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [setTokens, formData, isLogin, navigate, selectedUniversity, validateEmail]
  );

  const handleOptionSelect = (isLoginOption) => {
    setIsLogin(isLoginOption);
    setShowOptions(false);
  };

  const renderAuthForm = () => {
    if (!selectedUniversity) return null;

    if (selectedUniversity.auth_type === 'google_oauth') {
      return (
        <div className="mt-6">
          <p className="mb-4 text-sm text-gray-700">
            {selectedUniversity.name} uses Google authentication. Click the button below to
            continue.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Remember me checkbox for Google OAuth */}
          <div className="mb-4 flex items-center">
            <input
              id="googleRememberMe"
              name="rememberMe"
              type="checkbox"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-sm"
            />
            <label htmlFor="googleRememberMe" className="ml-2 block text-sm text-gray-900">
              Keep me signed in for 30 days
            </label>
          </div>

          {/* Terms agreement checkbox for Google OAuth */}
          <div className="mb-4 flex items-start">
            <div className="flex items-center h-5">
              <input
                id="googleAgreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded-sm"
              />
            </div>
            <div className="ml-2 text-sm">
              <label htmlFor="googleAgreeToTerms" className="text-gray-600">
                I agree to the{' '}
                <ExternalLink
                  href="/our-terms-of-service"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Terms of Service
                </ExternalLink>{' '}
                and{' '}
                <ExternalLink
                  href="/our-privacy-policy"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Privacy Policy
                </ExternalLink>
              </label>
            </div>
          </div>

          <div className="space-y-3">
            <GoogleLoginButton rememberMe={formData.rememberMe} disabled={!formData.agreeToTerms} />
            {error && (
              <button
                onClick={() => setError('')}
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-xs bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear Error & Try Again
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <form className="space-y-6" onSubmit={handleSubmit} noValidate>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {selectedUniversity.name === 'Other' ? 'Email' : 'School Email'}
          </label>
          <div className="mt-1">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder={
                selectedUniversity.name === 'Other'
                  ? 'Enter your email'
                  : `Enter your ${selectedUniversity.domain} email`
              }
            />
          </div>
          {selectedUniversity.domains &&
            selectedUniversity.domains.length > 0 &&
            selectedUniversity.name !== 'Other' && (
              <p className="mt-1 text-xs text-gray-500">
                {selectedUniversity.domains.length === 1
                  ? `Your email must end with @${selectedUniversity.domains[0]}`
                  : `Your email must end with one of: ${selectedUniversity.domains.map((d) => '@' + d).join(', ')}`}
              </p>
            )}
          {selectedUniversity.name === 'Other' && (
            <p className="mt-1 text-xs text-gray-500">Free trial users can use any email address</p>
          )}
        </div>

        {isLogin && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}

        {isLogin && (
          <div className="flex items-center">
            <input
              id="rememberMe"
              name="rememberMe"
              type="checkbox"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-sm"
            />
            <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
              Keep me signed in for 30 days
            </label>
          </div>
        )}

        {!isLogin && (
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded-sm"
              />
            </div>
            <div className="ml-2 text-sm">
              <label htmlFor="agreeToTerms" className="text-gray-600">
                I agree to the{' '}
                <ExternalLink
                  href="/our-terms-of-service"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Terms of Service
                </ExternalLink>{' '}
                and{' '}
                <ExternalLink
                  href="/our-privacy-policy"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Privacy Policy
                </ExternalLink>
              </label>
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading
              ? isLogin
                ? 'Signing in...'
                : 'Sending verification...'
              : isLogin
                ? 'Sign in'
                : 'Create account'}
          </button>
        </div>

        {isLogin && (
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>
          </div>
        )}
      </form>
    );
  };

  const renderOptions = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div
          className="bg-white p-6 rounded-lg shadow-md border-2 border-blue-100 hover:border-blue-300 cursor-pointer transition-all"
          onClick={() => handleOptionSelect(false)}
        >
          <div className="flex flex-col items-center text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 text-blue-500 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            <h3 className="text-xl font-bold text-gray-800 mb-2">I've never logged in before</h3>
            <p className="text-gray-600">Create a new account to get started</p>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-lg shadow-md border-2 border-blue-100 hover:border-blue-300 cursor-pointer transition-all"
          onClick={() => handleOptionSelect(true)}
        >
          <div className="flex flex-col items-center text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 text-blue-500 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            <h3 className="text-xl font-bold text-gray-800 mb-2">I've logged in before</h3>
            <p className="text-gray-600">Sign in to your existing account</p>
          </div>
        </div>
      </div>
    );
  };

  // Debug: Track error state changes (TEMP - remove after testing)
  useEffect(() => {
    if (error) {
      console.log('✅ AuthPage: Error displayed:', error);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src={process.env.PUBLIC_URL + '/maincar.png'}
            alt="InternHub Logo"
            className="h-14 mb-2"
          />
          <span className="text-blue-600 text-3xl font-bold ml-2 self-center">InternHub</span>
        </div>
        <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
          {showOptions
            ? "Let's get started! 🎉"
            : schoolCode && selectedUniversity && !isLogin
              ? `Create your ${selectedUniversity.name} account`
              : isLogin
                ? 'Sign in to your account'
                : 'Create your account'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
        <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
          {showOptions ? (
            renderOptions()
          ) : (
            <>
              <div className="mb-6">
                <label
                  htmlFor="university"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Select Your School
                </label>
                <p className="text-sm text-gray-500 mb-1">
                  Free trial users: please select "Other"
                </p>
                <p className="text-sm text-gray-500 mb-1">
                  If your school is not listed, please email support@internhubapp.com
                </p>
                <div className="relative">
                  <select
                    id="university"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs bg-white text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-10"
                    value={selectedUniversity?.id || ''}
                    onChange={handleUniversitySelect}
                  >
                    <option value="" disabled>
                      Select a school...
                    </option>
                    {universities.map((university) => (
                      <option key={university.id} value={university.id}>
                        {university.name === 'Other' ? 'Other (Free Trial)' : university.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {renderAuthForm()}

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowOptions(true)}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-xs bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  Go back to options
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
